import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Radio,
  Select,
  Table,
  TableColumnsType,
} from "antd";

import { useModal } from "@/hooks/modals/useModal";
import { PlusOutlined } from "@ant-design/icons";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useCancelReasonBehavior } from "../hooks/useCancelReasonBehavior";
import { useFormEditable } from "../hooks/useFormEditable";
import { useSourcingFollowBehavior } from "../hooks/useSourcingFollowBehavior";
import { useSourcingBy } from "../hooks/useSourcingBy";
import type {
  MaterialItem,
  SourcingRequirementApplicationFormFields,
} from "../type";
import { CancelReasonModal } from "./CancelReasonModal";
import styles from "./MaterialFormFields.module.less";
import { SourcingFollowModal } from "./sourcingNodeFollow/SouringFollowModal";
import { defaultNonProductionMaterialSourcingNodes } from "./sourcingNodeFollow/node.config";
export function NonProductionMaterialFormFields() {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const { isStartedEditable } = useFormEditable();

  const { props } = useModal("SOURCING_APPLICATION_20251202");
  const isStarted = props.mode === "started";

  const { isSourcingBy } = useSourcingBy(props.recordId);

  const form = Form.useFormInstance<SourcingRequirementApplicationFormFields>();

  const {
    cancelReasonModalOpen,
    sourcingMaterialId,
    handleCancelModalCancel,
    handleCancelModalConfirm,
    handleCancelSourcingChange,
  } = useCancelReasonBehavior(form);

  const {
    sourcingFollowModalOpen,
    currentSourcingMaterialId,
    handleViewSourcingProgress,
    handleCloseSourcingFollowModal,
  } = useSourcingFollowBehavior();

  const { reviewStatus, materialList } = form.getFieldsValue([
    "reviewStatus",
    "materialList",
  ]);

  const showCancelReason = materialList.some(
    (m: MaterialItem) => m.isSourcingCancel && !!m.cancelReason,
  );

  // 展示寻源执行列条件 reviewStatus === 4 已审批通过
  const showSourcingExecution = reviewStatus === "4";

  return (
    <Form.List name="materialList">
      {(fields, { add, remove }) => {
        const columns: TableColumnsType<any> = [
          {
            title: "序号",
            dataIndex: "materialCode",
            width: 80,
            align: "center",
            fixed: "left",
            render: (_, field, index) => {
              const isHover = hoverIndex === index;
              return isHover && isStartedEditable ? (
                <div
                  className={styles.removeIcon}
                  onClick={() => remove(index)}
                >
                  <Trash2 size={20} />
                </div>
              ) : (
                index + 1
              );
            },
          },

          {
            title: <span>寻源名称</span>,
            dataIndex: "materialName",
            align: "center",
            render: (_, field, index) => (
              <Form.Item<
                SourcingRequirementApplicationFormFields["materialList"]
              >
                name={[field.name, "materialName"]}
                rules={[{ required: true, message: "请输入物料名称" }]}
                noStyle
              >
                <Input placeholder="请输入" />
              </Form.Item>
            ),
          },
          {
            title: "需求数量",
            dataIndex: "count",
            align: "center",
            render: (_, field, index) => (
              <Form.Item<
                SourcingRequirementApplicationFormFields["materialList"]
              >
                name={[field.name, "count"]}
                noStyle
              >
                <Input placeholder="请输入" />
              </Form.Item>
            ),
          },

          {
            title: <span>要求完成日期</span>,
            dataIndex: "completeDate",
            align: "center",
            width: 200,
            render: (_, field, index) => (
              <Form.Item<
                SourcingRequirementApplicationFormFields["materialList"]
              >
                name={[field.name, "completeDate"]}
                rules={[{ required: true, message: "要求完成日期" }]}
                noStyle
              >
                <DatePicker />
              </Form.Item>
            ),
          },

          {
            title: <span>寻源原因</span>,
            dataIndex: "sourcingReason",
            align: "center",
            render: (_, field, index) => (
              <Form.Item<
                SourcingRequirementApplicationFormFields["materialList"]
              >
                noStyle
                name={[field.name, "sourcingReason"]}
              >
                <Input.TextArea rows={1} placeholder="请输入" />
              </Form.Item>
            ),
          },
          {
            title: <span>寻源要求</span>,
            dataIndex: "sourcingRequirements",
            align: "center",
            render: (_, field, index) => (
              <Form.Item<
                SourcingRequirementApplicationFormFields["materialList"]
              >
                name={[field.name, "sourcingRequirements"]}
                noStyle
              >
                <Input.TextArea rows={1} placeholder="请输入" />
              </Form.Item>
            ),
          },
          {
            title: "寻源优先级",
            dataIndex: "sourcingPrioritize",
            align: "center",
            width: 180,
            render: (_, field, index) => (
              <Form.Item<
                SourcingRequirementApplicationFormFields["materialList"]
              >
                name={[field.name, "sourcingPrioritize"]}
                noStyle
              >
                <Radio.Group
                  options={[
                    { value: 1, label: "紧急" },
                    { value: 2, label: "一般" },
                  ]}
                />
              </Form.Item>
            ),
          },
        ];

        // 审批通过后，加入需要寻源负责人填写的表单项
        if (showSourcingExecution) {
          // addSourcingByFormFieldsToColumns(columns, isSourcingBy);
        }

        columns.push({
          title: "取消寻源",
          dataIndex: "isSourcingCancel",
          align: "center",
          width: 100,
          render: (_, field, index) => {
            const isSourcingCancel = form.getFieldValue([
              "materialList",
              field.name,
              "isSourcingCancel",
            ]);

            /**
             * 同时满足以下条件时，允许编辑取消寻源复选框
             * 1. 当前审核状态为“完成”（reviewStatus === "4"）
             * 2. mode 为 started
             * 3. 该物料的取消寻源状态尚未设置
             */
            const isEditable =
              reviewStatus?.toString() === "4" &&
              isStarted &&
              !isSourcingCancel;

            return (
              <>
                <Form.Item<
                  SourcingRequirementApplicationFormFields["materialList"]
                >
                  name={[field.name, "isSourcingCancel"]}
                  noStyle
                  valuePropName="checked"
                >
                  <Checkbox
                    onChange={(e) =>
                      handleCancelSourcingChange(
                        e.target.checked,
                        index,
                        form.getFieldValue(["materialList", field.name, "id"]),
                      )
                    }
                    disabled={!isEditable}
                  ></Checkbox>
                </Form.Item>

                <Form.Item<
                  SourcingRequirementApplicationFormFields["materialList"]
                >
                  name={[field.name, "id"]}
                  hidden
                >
                  <Input placeholder="请输入" />
                </Form.Item>
              </>
            );
          },
        });

        if (showCancelReason) {
          columns.push({
            title: "取消原因",
            dataIndex: "cancelReason",
            align: "center",
            render: (_, field, index) => {
              return (
                <Form.Item<
                  SourcingRequirementApplicationFormFields["materialList"]
                >
                  name={[field.name, "cancelReason"]}
                  noStyle
                >
                  <Input.TextArea rows={1} placeholder="请输入" disabled />
                </Form.Item>
              );
            },
          });
        }

        if (showSourcingExecution) {
          columns.push({
            title: "寻源执行",
            dataIndex: "soucingExecution",
            align: "center",
            fixed: "right",
            width: 150,
            render: (_, field, index) => {
              const sourcingMaterialId = form.getFieldValue([
                "materialList",
                field.name,
                "id",
              ]);

              return (
                <Button
                  disabled={false}
                  onClick={() => handleViewSourcingProgress(sourcingMaterialId)}
                  variant="link"
                  color="primary"
                >
                  查看寻源进度
                </Button>
              );
            },
          });
        }

        return (
          <>
            <Table
              bordered
              dataSource={fields}
              pagination={false}
              scroll={{ x: 1200 }}
              onRow={(_, index) => ({
                onMouseEnter: () => setHoverIndex(index!),
                onMouseLeave: () => setHoverIndex(null),
              })}
              columns={columns}
            ></Table>

            <Button
              icon={<PlusOutlined />}
              hidden={!isStartedEditable}
              onClick={add}
              variant="outlined"
              color="primary"
              className={styles.addBtn}
            >
              添加
            </Button>

            <CancelReasonModal
              sourcingMaterialId={sourcingMaterialId}
              open={cancelReasonModalOpen}
              onClose={handleCancelModalCancel}
              onOk={handleCancelModalConfirm}
            />

            <SourcingFollowModal
              currentSourcingMaterialId={currentSourcingMaterialId!}
              defaultSourcingNodes={defaultNonProductionMaterialSourcingNodes}
              open={sourcingFollowModalOpen}
              onClose={handleCloseSourcingFollowModal}
            />
          </>
        );
      }}
    </Form.List>
  );
}

// todo：字段需要等后端确定好进行修改
function addSourcingByFormFieldsToColumns(
  columns: TableColumnsType<any>,
  isSourcingBy: boolean,
) {
  const list: TableColumnsType<any> = [
    {
      title: "寻源方法",
      dataIndex: "sourcingMethod",
      align: "center",
      width: 200,
      render: (_, field, index) => {
        return (
          <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
            name={[field.name, "sourcingMethod"]}
            noStyle
          >
            <Select
              disabled={!isSourcingBy}
              options={[
                {
                  label: "单一",
                  value: "单一",
                },
                {
                  label: "寻源",
                  value: "寻源",
                },
              ]}
            ></Select>
          </Form.Item>
        );
      },
    },
    {
      title: "计划开始时间",
      dataIndex: "planStartTime",
      align: "center",
      width: 200,
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "planStartTime"]}
          noStyle
        >
          <DatePicker disabled={!isSourcingBy} />
        </Form.Item>
      ),
    },
    {
      title: "计划结束时间",
      dataIndex: "planEndTime",
      align: "center",
      width: 200,
      render: (_, field, index) => (
        <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
          name={[field.name, "planEndTime"]}
          noStyle
        >
          <DatePicker disabled={!isSourcingBy} />
        </Form.Item>
      ),
    },

    {
      title: "单价（HKD）",
      dataIndex: "unitPrice",
      align: "center",
      width: 100,
      render: (_, field, index) => {
        return (
          <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
            name={[field.name, "unitPrice"]}
            noStyle
          >
            <Input placeholder="请输入" disabled={!isSourcingBy} />
          </Form.Item>
        );
      },
    },
    {
      title: "总金额（HKD）",
      dataIndex: "totalPrice",
      align: "center",
      width: 100,
      render: (_, field, index) => {
        return (
          <Form.Item<SourcingRequirementApplicationFormFields["materialList"]>
            name={[field.name, "totalPrice"]}
            noStyle
          >
            <Input placeholder="请输入" disabled={!isSourcingBy} />
          </Form.Item>
        );
      },
    },
  ];
  list.forEach((item) => columns.push(item));
}
